import React, { useEffect, useRef, useState } from 'react';
import { BankStatement, BankStatementFilters, bankStatementsAPI, StatementOption, StatementPage } from '../lib/api';
import { useTraderTime } from '../contexts/TraderTimeContext';
import { parseTimestamp } from '../lib/traderTime';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Pagination } from './ui/pagination';
import { Download, FileText, Loader2, Plus } from 'lucide-react';

type Audience = 'trader' | 'admin';
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const selectClass = 'min-w-0 w-full max-w-full rounded-md border border-input bg-background p-2 text-sm';
const dateLabel = (date: string) => date.split('-').reverse().join('.');

async function errorMessage(error: any): Promise<string> {
    let data = error.response?.data;
    if (data instanceof Blob) {
        try { data = JSON.parse(await data.text()); } catch { data = null; }
    }
    return typeof data?.detail === 'string' ? data.detail : 'Не удалось выполнить запрос. Попробуйте ещё раз.';
}

function TraderPicker({ value, onChange, disabled = false, forUpload = false }: {
    value: StatementOption | null; onChange: (value: StatementOption | null) => void; disabled?: boolean; forUpload?: boolean;
}) {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [result, setResult] = useState<StatementPage<StatementOption> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [retry, setRetry] = useState(0);
    const label = forUpload ? 'Трейдер для выписки' : 'Трейдер';
    const id = forUpload ? 'statement-upload-trader-choice' : 'statement-trader-choice';
    useEffect(() => {
        let active = true;
        setLoading(true); setError(''); setResult(null);
        const timer = window.setTimeout(() => {
            bankStatementsAPI.traders(search.trim(), page).then(data => {
                if (active) setResult(data);
            }).catch(async err => {
                const message = await errorMessage(err);
                if (active) setError(message);
            }).finally(() => { if (active) setLoading(false); });
        }, search ? 250 : 0);
        return () => { active = false; window.clearTimeout(timer); };
    }, [search, page, retry]);
    const options = result?.items || [];
    return <div className="min-w-0 space-y-2">
        <Label htmlFor={id}>{label}</Label>
        <Input aria-label={`Поиск: ${label}`} placeholder="Поиск по имени трейдера"
            value={search} maxLength={200} disabled={disabled} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select id={id} aria-label={label} className={selectClass} value={value?.id || ''} required={forUpload}
            disabled={disabled || loading} onChange={e => onChange(options.find(item => item.id === e.target.value) || null)}>
            <option value="">{forUpload ? 'Выберите трейдера' : 'Все трейдеры'}</option>
            {value && !options.some(item => item.id === value.id) && <option value={value.id}>{value.label}</option>}
            {options.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
        </select>
        {loading && <p role="status" className="text-sm text-muted-foreground">Загрузка списка…</p>}
        {!loading && !error && options.length === 0 && <p className="text-sm text-muted-foreground">Ничего не найдено</p>}
        {error && <div role="alert" className="text-sm text-red-600">{error} <button type="button" onClick={() => setRetry(n => n + 1)}>Повторить</button></div>}
        {result && result.pages > 1 && <div className="flex items-center gap-2 text-sm">
            <Button type="button" size="sm" variant="outline" disabled={disabled || loading || page === 1} onClick={() => setPage(n => n - 1)} aria-label={`Предыдущие: ${label}`}>←</Button>
            <span>{page} / {result.pages}</span>
            <Button type="button" size="sm" variant="outline" disabled={disabled || loading || page === result.pages} onClick={() => setPage(n => n + 1)} aria-label={`Следующие: ${label}`}>→</Button>
        </div>}
    </div>;
}

function StatementForm({ audience, initialTrader, onSaved, onCancel }: {
    audience: Audience; initialTrader: StatementOption | null; onSaved: (item: BankStatement) => void; onCancel: () => void;
}) {
    const [selectedTrader, setSelectedTrader] = useState<StatementOption | null>(initialTrader);
    const [requisiteName, setRequisiteName] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [comment, setComment] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submitting = useRef(false);
    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (submitting.current) return;
        if (audience === 'admin' && !selectedTrader) { setError('Выберите трейдера'); return; }
        if (!requisiteName.trim() || requisiteName.trim().length > 200) { setError('Название реквизита должно содержать от 1 до 200 символов'); return; }
        if (!from || !to || !file) { setError('Укажите период и выберите файл'); return; }
        if (from > to) { setError('Начало периода должно быть не позже окончания'); return; }
        if (!/\.(pdf|csv|xlsx)$/i.test(file.name)) { setError('Разрешены только PDF, CSV и XLSX'); return; }
        if (!file.size || file.size > MAX_FILE_SIZE) { setError('Файл должен быть непустым и не больше 20 МБ'); return; }
        const data = new FormData();
        if (audience === 'admin' && selectedTrader) data.append('trader_id', selectedTrader.id);
        data.append('requisite_label', requisiteName.trim()); data.append('period_from', from); data.append('period_to', to);
        data.append('comment', comment); data.append('file', file);
        submitting.current = true; setSaving(true); setError('');
        try { const item = await bankStatementsAPI.create(data, audience); onSaved(item); }
        catch (err) { setError(await errorMessage(err)); }
        finally { submitting.current = false; setSaving(false); }
    };
    return <Card><CardHeader><CardTitle>Добавить выписку</CardTitle></CardHeader><CardContent>
        <form onSubmit={submit} className="space-y-4">
            {audience === 'admin' && <TraderPicker forUpload value={selectedTrader} onChange={setSelectedTrader} disabled={saving} />}
            <div className="grid gap-4 sm:grid-cols-2">
                <div><Label htmlFor="statement-from">Период выписки: с</Label><Input id="statement-from" aria-label="Период выписки: с" type="date" required value={from} disabled={saving} onChange={e => setFrom(e.target.value)} /></div>
                <div><Label htmlFor="statement-to">Период выписки: по</Label><Input id="statement-to" aria-label="Период выписки: по" type="date" required value={to} disabled={saving} onChange={e => setTo(e.target.value)} /></div>
            </div>
            <div><Label htmlFor="statement-requisite-name">Название реквизита</Label>
                <Input id="statement-requisite-name" aria-label="Название реквизита" placeholder="Например, ФИ, реквизит №123456, карта" required maxLength={200}
                    value={requisiteName} disabled={saving} onChange={e => setRequisiteName(e.target.value)} /></div>
            <div><Label htmlFor="statement-file">Файл выписки</Label><Input id="statement-file" aria-label="Файл выписки" type="file" required accept=".pdf,.csv,.xlsx" disabled={saving} onChange={e => setFile(e.target.files?.[0] || null)} />
                <p className="mt-1 text-sm text-muted-foreground">Один файл PDF, CSV или XLSX, до 20 МБ.</p></div>
            <div><Label htmlFor="statement-comment">Комментарий (необязательно)</Label><textarea id="statement-comment" aria-label="Комментарий" className={selectClass} rows={3} maxLength={2000} value={comment} disabled={saving} onChange={e => setComment(e.target.value)} />
                <p className="text-right text-xs text-muted-foreground">{comment.length} / 2000</p></div>
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">После загрузки выписку, её поля и файл нельзя изменить или удалить. Для исправления добавьте новую выписку.</p>
            {error && <p role="alert" className="text-red-600 dark:text-red-400">{error}</p>}
            <div className="flex gap-2"><Button type="submit" disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{saving ? 'Загрузка…' : 'Загрузить выписку'}</Button>
                <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>Отмена</Button></div>
        </form>
    </CardContent></Card>;
}

export function BankStatements({ audience, canManage = false }: { audience: Audience; canManage?: boolean }) {
    const { formatDate } = useTraderTime();
    const [showForm, setShowForm] = useState(false);
    const [page, setPage] = useState(1);
    const [result, setResult] = useState<StatementPage<BankStatement> | null>(null);
    const [filters, setFilters] = useState<BankStatementFilters>({});
    const [trader, setTrader] = useState<StatementOption | null>(null);
    const [initialUploadTrader, setInitialUploadTrader] = useState<StatementOption | null>(null);
    const [currency, setCurrency] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterError, setFilterError] = useState('');
    const [notice, setNotice] = useState('');
    const [refresh, setRefresh] = useState(0);
    const [downloading, setDownloading] = useState<string | null>(null);
    const [downloadError, setDownloadError] = useState('');
    useEffect(() => {
        let active = true;
        setLoading(true); setError(''); setResult(null);
        bankStatementsAPI.list(audience, { ...filters, page, size: 20 }).then(data => {
            if (active) setResult(data);
        }).catch(async err => {
            const message = await errorMessage(err);
            if (active) setError(message);
        }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [audience, filters, page, refresh]);
    const applyFilters = (event: React.FormEvent) => {
        event.preventDefault();
        if (from && to && from > to) { setFilterError('Начало периода должно быть не позже окончания'); return; }
        setFilterError(''); setPage(1);
        setFilters({
            ...(trader ? { trader_id: trader.id } : {}), ...(currency ? { currency } : {}),
            ...(from ? { period_from: from } : {}), ...(to ? { period_to: to } : {})
        });
    };
    const download = async (item: BankStatement) => {
        setDownloading(item.id); setDownloadError('');
        try {
            const blob = await bankStatementsAPI.download(audience, item.id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = url; link.download = item.original_filename;
            document.body.appendChild(link); link.click(); link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) { setDownloadError(await errorMessage(err)); }
        finally { setDownloading(null); }
    };
    return <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-2xl font-bold"><FileText className="h-6 w-6" />Банковские выписки</h2>
            <div className="flex gap-2"><Button variant="outline" disabled={loading} onClick={() => setRefresh(n => n + 1)}>Обновить</Button>
                {(audience === 'trader' || canManage) && !showForm && <Button onClick={() => { setInitialUploadTrader(audience === 'admin' ? trader : null); setShowForm(true); setNotice(''); }}><Plus className="mr-2 h-4 w-4" />Добавить выписку</Button>}</div>
        </div>
        {notice && <p role="status" className="text-green-700 dark:text-green-300">{notice}</p>}
        {showForm && <StatementForm audience={audience} initialTrader={initialUploadTrader} onCancel={() => setShowForm(false)} onSaved={item => {
            const hidden = audience === 'admin' && Boolean(
                (filters.trader_id && filters.trader_id !== item.trader_id) ||
                (filters.currency && filters.currency !== item.currency) ||
                (filters.period_from && filters.period_from > item.period_to) ||
                (filters.period_to && filters.period_to < item.period_from)
            );
            setShowForm(false); setPage(1); setRefresh(n => n + 1);
            setNotice(hidden ? 'Выписка загружена, но текущие фильтры скрывают её.' : 'Выписка загружена');
        }} />}
        {audience === 'admin' && <Card><CardContent className="pt-6"><form onSubmit={applyFilters} className="space-y-4">
            <div className="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <TraderPicker value={trader} onChange={setTrader} />
                <div><Label htmlFor="statement-currency">Валюта</Label><select id="statement-currency" aria-label="Валюта" className={selectClass} value={currency} onChange={e => setCurrency(e.target.value)}>
                    <option value="">Все валюты</option>{['RUB', 'KGS', 'KZT'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><Label htmlFor="statement-filter-from">Период выписки: с</Label><Input id="statement-filter-from" aria-label="Период выписки: с" type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
                <div><Label htmlFor="statement-filter-to">Период выписки: по</Label><Input id="statement-filter-to" aria-label="Период выписки: по" type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
            </div>
            <p className="text-sm text-muted-foreground">Поиск по пересечению периодов выписок, включая граничные даты.</p>
            {filterError && <p role="alert" className="text-red-600">{filterError}</p>}
            <div className="flex gap-2"><Button type="submit">Применить</Button><Button type="button" variant="outline" onClick={() => {
                setTrader(null); setCurrency(''); setFrom(''); setTo(''); setFilterError(''); setFilters({}); setPage(1);
            }}>Сбросить</Button></div>
        </form></CardContent></Card>}
        {downloadError && <p role="alert" className="text-red-600">{downloadError}</p>}
        <Card><CardContent className="pt-6">
            {loading && <p role="status">Загрузка выписок…</p>}
            {error && <div role="alert" className="text-red-600">{error} <Button variant="outline" onClick={() => setRefresh(n => n + 1)}>Повторить</Button></div>}
            {result && <>
                {result.items.length === 0 ? <p className="py-8 text-center text-muted-foreground">Выписки не найдены</p> : <div className="overflow-x-auto"><table className="w-full text-left text-sm">
                    <thead><tr className="border-b">{[...(audience === 'admin' ? ['Трейдер'] : []), 'Валюта', 'Название реквизита', 'Период выписки', 'Дата загрузки', 'Файл', 'Комментарий', ...(audience === 'admin' ? ['Кто добавил'] : []), ''].map((title, i) => <th key={i} className="p-3 font-medium">{title}</th>)}</tr></thead>
                    <tbody>{result.items.map(item => <tr key={item.id} className="border-b last:border-0">
                        {audience === 'admin' && <td className="p-3">{item.trader_username}</td>}
                        <td className="p-3">{item.currency}</td><td className="min-w-[200px] max-w-sm break-words p-3">{item.requisite_label}</td>
                        <td className="whitespace-nowrap p-3">{dateLabel(item.period_from)} – {dateLabel(item.period_to)}</td>
                        <td className="whitespace-nowrap p-3">{audience === 'trader' ? formatDate(item.created_at) : parseTimestamp(item.created_at).toLocaleString('ru-RU')}</td>
                        <td className="min-w-[140px] max-w-xs break-words p-3">{item.original_filename}<div className="text-xs text-muted-foreground">{(item.file_size / 1024 / 1024).toFixed(2)} МБ</div></td>
                        <td className="min-w-[160px] max-w-sm whitespace-pre-wrap break-words p-3">{item.comment || '—'}</td>
                        {audience === 'admin' && <td className="p-3">{item.author_username}{item.author_role === 'admin' && <div className="text-xs text-muted-foreground">Администратор</div>}</td>}
                        <td className="p-3"><Button size="sm" variant="outline" disabled={downloading !== null} onClick={() => download(item)} aria-label={`Скачать ${item.original_filename}`}>
                            {downloading === item.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}Скачать</Button></td>
                    </tr>)}</tbody>
                </table></div>}
                <Pagination currentPage={page} totalPages={result.pages} totalItems={result.total} pageSize={20} onPageChange={setPage} />
            </>}
        </CardContent></Card>
    </div>;
}
